import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, ArrowUpRight } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { to: "/sobre", label: "Sobre" },
    { to: "/servicos", label: "Serviços" },
    { to: "/planos", label: "Planos" },
    { to: "/mentoria", label: "Mentoria" },
    { to: "/contato", label: "Contato" },
  ];

  const services = [
    "Recrutamento e Seleção",
    "Avaliação Psicológica",
    "Desenvolvimento de Liderança",
    "Treinamentos Corporativos",
    "Consultoria em Gestão de Pessoas",
  ];

  return (
    <footer className="bg-brand-neutro text-brand-base-clara" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-brand-terra-humana/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-serif text-3xl lg:text-4xl font-normal mb-4">
                Pronto para transformar sua equipe?
              </h2>
              <p className="font-sans text-brand-terra-humana text-base lg:text-lg leading-relaxed">
                Agende um diagnóstico estratégico gratuito e descubra como alinhar pessoas, cultura e resultados.
              </p>
            </div>
            <a
              href="https://wa.me/5586998325746?text=Olá! Gostaria de agendar um diagnóstico estratégico para minha empresa."
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-brand-terracota hover:bg-brand-terra-humana text-brand-base-clara px-8 py-4 rounded-xl font-sans font-medium transition-premium"
              data-testid="footer-cta-button"
            >
              Agendar Diagnóstico
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <span className="font-serif text-2xl font-semibold text-brand-base-clara">
                Lylian Lopes
              </span>
              <span className="block font-sans text-sm text-brand-terra-humana mt-1">
                Consultoria em Psicologia Organizacional
              </span>
            </Link>
            <p className="font-sans text-sm text-brand-terra-humana/80 leading-relaxed mb-6">
              Psicologia organizacional aplicada para alinhar pessoas, cultura e resultados com mais clareza, saúde e estratégia.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/lylopespsi"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-terra-humana/20 flex items-center justify-center hover:bg-brand-terracota transition-premium"
                data-testid="footer-instagram"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a
                href="mailto:contato@lylianlopesconsultoria.com.br"
                className="w-10 h-10 rounded-full bg-brand-terra-humana/20 flex items-center justify-center hover:bg-brand-terracota transition-premium"
                data-testid="footer-email"
              >
                <Mail size={18} strokeWidth={1.5} />
              </a>
              <a
                href="https://wa.me/5586998325746"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-terra-humana/20 flex items-center justify-center hover:bg-brand-terracota transition-premium"
                data-testid="footer-whatsapp"
              >
                <Phone size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-sans text-sm font-semibold uppercase tracking-wider text-brand-terra-humana mb-6">
              Navegação
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="font-sans text-sm text-brand-base-clara/80 hover:text-brand-terracota transition-premium"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-sans text-sm font-semibold uppercase tracking-wider text-brand-terra-humana mb-6">
              Serviços
            </h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/servicos"
                    className="font-sans text-sm text-brand-base-clara/80 hover:text-brand-terracota transition-premium"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-sans text-sm font-semibold uppercase tracking-wider text-brand-terra-humana mb-6">
              Contato
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:contato@lylianlopesconsultoria.com.br"
                  className="font-sans text-sm text-brand-base-clara/80 hover:text-brand-terracota transition-premium flex items-center gap-2"
                  data-testid="footer-contact-email"
                >
                  <Mail size={16} strokeWidth={1.5} />
                  contato@lylianlopesconsultoria.com.br
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/5586998325746"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-sm text-brand-base-clara/80 hover:text-brand-terracota transition-premium flex items-center gap-2"
                  data-testid="footer-contact-phone"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  (86) 99832-5746
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com/lylopespsi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-sm text-brand-base-clara/80 hover:text-brand-terracota transition-premium flex items-center gap-2"
                  data-testid="footer-contact-instagram"
                >
                  <Instagram size={16} strokeWidth={1.5} />
                  @lylopespsi
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-brand-terra-humana/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-sans text-xs text-brand-terra-humana/60">
              © {currentYear} Lylian Lopes Consultoria. Todos os direitos reservados.
            </p>
            <p className="font-sans text-xs text-brand-terra-humana/60">
              Psicologia Organizacional · Teresina, PI
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
